import { TERMINAL_CONVERSATION_STATUSES as TERMINAL_CONVERSATION_STATUS_LIST } from '../conversations/terminal-status';
// A cópia local dos 4 terminais saiu em 15/Ago/2026 — a lista virou domicílio
// único em `../conversations/terminal-status`. O `Set` fica para não trocar o
// custo de busca deste adapter, que roda sobre a worklist inteira.
const TERMINAL_CONVERSATION_STATUSES = new Set(TERMINAL_CONVERSATION_STATUS_LIST);
const SHOWN_APPOINTMENT_STATUSES = new Set(['scheduled', 'confirmed', 'imminent']);
const PRIORITY_RANK = {
    p0: 0,
    p1: 1,
    p2: 2,
    none: 3,
};
// S4 Onda 4 (revisão adversarial): `awaiting_claim` (fila SEGURADA pelo modo
// recepcionista, gated) entra no handback — sem o selo, a conversa segurada era
// card comum e ninguém era sinalizado a dar o claim (a única saída espontânea).
const ESCALATION_HANDBACK = new Set([
    'awaiting_consent',
    'confirmed_pending_handoff',
    'awaiting_claim',
]);
// "Retomar" (takeOver, H_c→H_a) só é válido enquanto a IA AINDA conduz —
// `awaiting_consent` (pediu consentimento ao cliente, segue em H_c). Em
// `confirmed_pending_handoff` o cliente já aceitou e a IA JÁ devolveu (H_a):
// takeOver retornaria 409 Conflict ("a IA já não está conduzindo"; Item 376a).
// Nesse caso o operador assume (se órfã) ou abre (se já é dele) — e o backend
// limpa o escalationState ao assumir (Item 376c), tirando o card do handback.
// (Item 371 fix, 14/Jun/2026.)
// S4 Onda 4: `awaiting_claim` mantém H_c (a IA segura a fila) → takeOver é
// válido; o claim também destrava (flip no claim + self-claim idempotente).
const ESCALATION_RETOMAR_VALID = new Set(['awaiting_consent', 'awaiting_claim']);
function convKind(c) {
    if (c.escalationState != null && ESCALATION_HANDBACK.has(c.escalationState))
        return 'ai';
    if (c.operationalPriority === 'p0' || c.operationalPriority === 'p1')
        return 'urgent';
    // S2 PR-b (Corrente de Compromissos, design §2) — compromisso VENCIDO sobe:
    // o item NUNCA é maré baixa (kind='urgent' já basta — `lowTide` só se
    // aplica a kind='normal', vide buildWorklist).
    if (c.promise?.overdue === true)
        return 'urgent';
    return 'normal';
}
/** 90 → "1h", 2880 → "2d", 45 → "45 min". Piso honesto (floor), nunca superestima. */
export function humanizeMinutes(min) {
    if (min < 60)
        return `${min} min`;
    if (min < 2880)
        return `${Math.floor(min / 60)}h`;
    return `${Math.floor(min / 1440)}d`;
}
const LAST_MESSAGE_PREFIX = {
    customer: '',
    attendant: 'você: ',
    ai: 'IA: ',
    system: '',
};
function truncate(text, max) {
    const clean = text.replace(/\s+/g, ' ').trim();
    return clean.length <= max ? clean : `${clean.slice(0, max - 1)}…`;
}
// Corrente P0 (kickoff 2026-07-10): o why SEMPRE responde "por que este item
// está aqui" — sinais excepcionais primeiro, depois espera (semântica ADR-106:
// awaitingPartyType='operator' ⇒ o CLIENTE espera por nós — o caso urgente),
// depois o assunto (última mensagem). Nunca retorna vazio.
function convWhy(c, nowMs) {
    const parts = [];
    if (c.escalationState != null && ESCALATION_HANDBACK.has(c.escalationState)) {
        parts.push('a IA devolveu esta conversa para um humano');
    }
    // S2 PR-b (design §2) — promessa VENCIDA é sinal excepcional: "você
    // prometeu {what} há {tempo}" + ("; cliente voltou {N}×" quando >0),
    // como UM único átomo do why (não dois `parts` — o "；" é interno).
    // No prazo: NÃO muda o why (só a pill informa — anti-overwhelming).
    if (c.promise?.overdue === true) {
        const promisedMs = Date.parse(c.promise.promisedAtIso);
        const agoMinutes = Number.isFinite(promisedMs)
            ? Math.max(0, Math.floor((nowMs - promisedMs) / 60000))
            : 0;
        let atom = `você prometeu ${c.promise.what} há ${humanizeMinutes(agoMinutes)}`;
        if (c.promise.customerReturns > 0) {
            atom += `; cliente voltou ${c.promise.customerReturns}×`;
        }
        parts.push(atom);
    }
    if (c.commitment?.broken === true)
        parts.push('compromisso do cliente quebrado');
    if (typeof c.conflictCount === 'number' && c.conflictCount > 0)
        parts.push('alerta de conflito ativo');
    if (c.qualitySignals?.sentimentTrend === 'descending')
        parts.push('tom em queda');
    if (typeof c.awaitingDurationMinutes === 'number') {
        if (c.awaitingPartyType === 'operator') {
            parts.push(`cliente espera há ${humanizeMinutes(c.awaitingDurationMinutes)}`);
        }
        else if (c.awaitingPartyType === 'customer') {
            parts.push(`aguardando resposta do cliente há ${humanizeMinutes(c.awaitingDurationMinutes)}`);
        }
    }
    if (c.lastMessage != null && c.lastMessage.content.trim() !== '') {
        const prefix = LAST_MESSAGE_PREFIX[c.lastMessage.origin] ?? '';
        parts.push(`${prefix}«${truncate(c.lastMessage.content, 64)}»`);
    }
    if (parts.length === 0) {
        const createdMs = Date.parse(c.createdAt);
        if (Number.isFinite(createdMs) && createdMs <= nowMs) {
            parts.push(`aberta há ${humanizeMinutes(Math.floor((nowMs - createdMs) / 60000))}`);
        }
        else {
            parts.push('sem mensagens ainda');
        }
    }
    return parts.join(' · ');
}
const CHANNEL_LABEL = {
    'whatsapp-cloud-api': 'WhatsApp',
    webchat: 'Webchat',
    email: 'E-mail',
    telegram: 'Telegram',
    'instagram-dm': 'Instagram',
    simulador: 'Simulador',
};
function convPills(c, variant, nowMs) {
    const pills = [];
    if (c.operationalPriority === 'p0' || c.operationalPriority === 'p1') {
        pills.push({ kind: 'prio', label: 'alta' });
    }
    if (c.escalationState != null && ESCALATION_HANDBACK.has(c.escalationState)) {
        pills.push({ kind: 'ai', label: '↩ IA → você' });
    }
    if (variant === 'regulated' && (c.classificationLevel ?? 0) >= 3) {
        pills.push({ kind: 'sens', label: 'sensível' });
    }
    // S2 PR-b (design §2) — pill sempre presente com compromisso aberto,
    // vencida ou não (o why só fala quando vencida; a pill informa sempre).
    if (c.promise != null) {
        if (c.promise.overdue) {
            pills.push({ kind: 'promise', label: 'promessa vencida' });
        }
        else if (c.promise.dueAtIso !== null) {
            const dueMinutes = minutesUntil(c.promise.dueAtIso, nowMs);
            pills.push({
                kind: 'promise',
                label: `promessa até ${humanizeMinutes(Math.max(0, dueMinutes))}`,
            });
        }
        else {
            pills.push({ kind: 'promise', label: 'promessa aberta' });
        }
    }
    // Corrente P0: canal sempre visível — triagem sem abrir exige saber onde a conversa vive.
    pills.push({ kind: 'canal', label: CHANNEL_LABEL[c.channel] ?? c.channel });
    // S4 PR-b (Corrente de Compromissos, Item 404 faceta a) — pill NEUTRA quando
    // o cliente tem dono de carteira (Item 377). Primeiro nome só (card denso);
    // sem nome, rótulo genérico honesto — nunca o ULID cru (revisão N1).
    if (c.portfolio != null) {
        const firstName = c.portfolio.operatorName?.split(' ')[0];
        pills.push({
            kind: 'portfolio',
            label: firstName !== undefined && firstName !== ''
                ? `carteira de ${firstName}`
                : 'carteira atribuída',
        });
    }
    return pills;
}
function convActions(c) {
    // Retomar só enquanto a IA conduz (awaiting_consent / H_c). Handback já
    // confirmado (H_a) cai na ação normal: assumir (órfã) ou abrir (já é dele).
    if (c.escalationState != null && ESCALATION_RETOMAR_VALID.has(c.escalationState)) {
        return [{ kind: 'retomar', label: 'Retomar', primary: true, targetId: c.id }];
    }
    const actions = [{ kind: 'abrir', label: 'Abrir', targetId: c.id }];
    if (c.assignedToUserId == null) {
        actions.push({ kind: 'assumir', label: 'Assumir', primary: true, targetId: c.id });
    }
    return actions;
}
function minutesUntil(iso, nowMs) {
    return Math.round((Date.parse(iso) - nowMs) / 60000);
}
/**
 * S5 (maré baixa produtiva) — gestos da sugestão. «Fazer agora» navega à
 * conversa (kind `fazer-agora`) OU à ficha da Pessoa quando não há conversa
 * aberta (kind `ver-pessoa`, honest v1 — NÃO cria conversa proativa nova).
 * «Delegar à IA» só existe com conversa aberta (o FE ainda filtra por cap
 * `ai-step-delegation` — o adapter é agnóstico de capability). «Agora não»
 * sempre presente (dismiss).
 */
function suggestionActions(s) {
    const actions = [];
    if (s.conversationId !== undefined) {
        actions.push({
            kind: 'fazer-agora',
            label: 'Fazer agora',
            primary: true,
            targetId: s.conversationId,
            personId: s.personId,
            suggestionId: s.id,
        });
        actions.push({
            kind: 'delegar',
            label: 'Delegar à IA',
            targetId: s.conversationId,
            personId: s.personId,
            suggestionId: s.id,
        });
    }
    else {
        actions.push({
            kind: 'ver-pessoa',
            label: 'Fazer agora',
            primary: true,
            targetId: s.personId,
            personId: s.personId,
            suggestionId: s.id,
        });
    }
    actions.push({
        kind: 'dispensar',
        label: 'Agora não',
        targetId: s.id,
        personId: s.personId,
        suggestionId: s.id,
    });
    return actions;
}
const SUGGESTION_ICON = {
    M6: 'fi--pendency',
    M7: 'fi--sentiment',
};
function suggestionItem(s) {
    const why = s.paidTemplateRisk ? `${s.why} · pode exigir template pago` : s.why;
    return {
        id: s.id,
        kind: 'suggestion',
        iconKind: SUGGESTION_ICON[s.mina],
        title: s.personName,
        pills: [{ kind: 'suggestion', label: s.valueLabel }],
        why,
        actions: suggestionActions(s),
        lowTide: true,
        ...(s.conversationId !== undefined ? { conversationId: s.conversationId } : {}),
    };
}
export function buildWorklist(input) {
    const { conversations, appointments, variant, nowMs, suggestions } = input;
    const activeConversations = conversations.filter((c) => !TERMINAL_CONVERSATION_STATUSES.has(c.status));
    const convItems = activeConversations.map((c) => {
        const kind = convKind(c);
        const waitMinutes = c.awaitingPartyType === 'operator' && typeof c.awaitingDurationMinutes === 'number'
            ? c.awaitingDurationMinutes
            : undefined;
        return {
            id: `conv:${c.id}`,
            kind,
            iconKind: kind === 'ai' ? 'fi--ai' : 'fi--person',
            title: c.person?.displayName ?? 'Cliente sem identificação',
            pills: convPills(c, variant, nowMs),
            why: convWhy(c, nowMs),
            actions: convActions(c),
            lowTide: kind === 'normal' && (c.operationalPriority === 'none' || c.operationalPriority === 'p2'),
            conversationId: c.id,
            ...(waitMinutes !== undefined ? { waitMinutes } : {}),
        };
    });
    const apptItems = appointments
        .filter((a) => SHOWN_APPOINTMENT_STATUSES.has(a.status))
        .map((a) => {
        const mins = minutesUntil(a.scheduledAt, nowMs);
        return {
            id: `appt:${a.id}`,
            kind: 'soon',
            iconKind: 'fi--cal',
            title: a.title,
            pills: mins >= 0 ? [{ kind: 'soon', label: `em ${mins} min` }] : [],
            why: '',
            actions: a.status === 'scheduled'
                ? [{ kind: 'confirmar', label: 'Confirmar', primary: true, targetId: a.id }]
                : [{ kind: 'comunicar', label: 'Ver na agenda', targetId: a.id }],
            lowTide: false,
        };
    });
    const sortedConvs = [...activeConversations]
        .map((c, idx) => ({ c, idx }))
        .sort((a, b) => PRIORITY_RANK[a.c.operationalPriority] - PRIORITY_RANK[b.c.operationalPriority] ||
        a.idx - b.idx)
        .map(({ c }) => convItems.find((i) => i.id === `conv:${c.id}`));
    const high = sortedConvs.filter((i) => !i.lowTide);
    // Corrente P0: a maré baixa ordena por quem espera há mais tempo (o vazio
    // nunca mente — a prévia do fallback lê daqui). Sem espera medida → fim.
    const low = sortedConvs
        .filter((i) => i.lowTide)
        .sort((a, b) => (b.waitMinutes ?? -1) - (a.waitMinutes ?? -1));
    // S5 (maré baixa produtiva) — sugestões SEMPRE ao final (oferta, não
    // urgência); ausente/`[]` ⇒ byte-idêntico ao pré-S5.
    const suggestionItems = (suggestions ?? []).map(suggestionItem);
    return [...high, ...apptItems, ...low, ...suggestionItems];
}
//# sourceMappingURL=worklist-adapter.js.map